import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SCHEMA } from "@/admin/contentSchema";
import { Field } from "@/admin/ContentField";
import { cn } from "@/lib/utils";

export default function ContentEditor() {
  const { content, refresh } = useSite();
  const [active, setActive] = useState(SCHEMA[0].key);
  const [draft, setDraft] = useState(content[SCHEMA[0].key]);
  const [busy, setBusy] = useState(false);
  const section = SCHEMA.find((s) => s.key === active);

  useEffect(() => { setDraft(content[active]); }, [active, content]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/content/${active}`, { data: draft });
      await refresh();
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-content-page">
      <h1 className="text-2xl font-bold text-charcoal">Website Content</h1>
      <p className="text-sm text-muted-foreground mb-6">Pick a section, edit, then press Save. Changes go live on Save.</p>
      <div className="grid gap-6 lg:grid-cols-[240px_1fr] items-start">
        <nav className="rounded-xl border bg-white p-2 lg:sticky lg:top-6 flex lg:flex-col overflow-x-auto gap-0.5" data-testid="content-section-nav">
          {SCHEMA.map((s) => (
            <button key={s.key} type="button" onClick={() => setActive(s.key)} className={cn("text-left rounded-lg px-3 py-2 text-sm whitespace-nowrap", active === s.key ? "bg-brand text-white" : "hover:bg-muted")} data-testid={`content-section-${s.key}`}>
              {s.title}
            </button>
          ))}
        </nav>
        <div className="rounded-xl border bg-white p-5 sm:p-7 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-6 sticky top-0 bg-white z-10 py-2 -mt-2 border-b">
            <h2 className="text-lg font-bold" data-testid="content-section-title">{section.title}</h2>
            <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="content-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save</Button>
          </div>
          <div className="space-y-6" key={active}>
            {section.fields.map((f) => (
              <Field key={f.k} f={f} value={draft?.[f.k]} onChange={(v) => setDraft((d) => ({ ...d, [f.k]: v }))} id={`field-${active}-${f.k}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
